import { computed, ref } from "vue";
import type { AgentDisplay } from "@/shared/workspace/use-workspace-doc";
import { useAgentDisplay } from "@/shared/workspace/use-workspace-doc";
import {
	type AgentDisplayAxis,
	type AgentDisplayMode,
	DEFAULT_CLIENT_MODE,
	DEFAULT_POLICY,
	usePlatformAccessDoc,
} from "./policy";

/**
 * O QUE ESTA PESSOA VÊ do trabalho do agente — a resolução inteira, num sítio.
 *
 * ── O PRINCÍPIO, HERDADO DA V2 ────────────────────────────────────────────
 *
 *   "O desenho é conduzido INTEIRAMENTE por configuração — nunca pelo papel."
 *
 * Nenhum componente pergunta "sou admin?" para decidir desenhar. O papel escolhe
 * QUAL LISTA de eixos se aplica (`AGENT_DISPLAY_CLIENT` ou `AGENT_DISPLAY_DEV`,
 * duas linhas de `.env` no servidor). Consumo em modo cliente é sempre em créditos.
 * Mudar o que um dev vê é editar uma linha de configuração, não caçar `v-if`
 * por seis componentes.
 *
 * ── AS DUAS PORTAS, E POR QUE SÃO DUAS ────────────────────────────────────
 *
 * A política da INSTALAÇÃO diz o que é possível mostrar a cada audiência. Os três
 * interruptores do WORKSPACE (`agentDisplay`, a tela de conta) dizem o que a
 * equipa desta conta quer ver. O interruptor DESLIGADO apaga sempre — um
 * workspace que desligou "mostrar raciocínio" não passa a vê-lo porque a
 * instalação o permite, e isso vale também para o dev, de propósito: um
 * administrador da plataforma a olhar para a conta de alguém deve vê-la como ela
 * está configurada.
 *
 * Ligado, o interruptor manda no que ele NOMEIA e a política manda no resto — ver
 * `SWITCH_GRANTS`. É a metade que faltava: sem ela, "Mostrar o consumo" era um
 * interruptor que nenhuma tela obedecia (BUGS.md 05-2). O que o modo DEV
 * acrescenta continua a ser PROFUNDIDADE (abrir o passo, ler o trace, ver o
 * prompt), e nada disso um interruptor de três posições consegue conceder.
 */

export type Audience = "client" | "dev";

/** Qual interruptor do workspace manda em cada eixo. Um eixo sem entrada aqui não
 *  é vetável pelo cliente — a hora e a barra de ações são a conversa, não o
 *  trabalho do agente. */
const TENANT_SWITCH: Partial<Record<AgentDisplayAxis, "showReasoning" | "showTools" | "showCost">> =
	{
		reasoning: "showReasoning",
		reasoningRaw: "showReasoning",
		// Sob o mesmo interruptor que o raciocínio cru, e pela mesma razão: um dono
		// que desligou "mostrar raciocínio" na conta dele não passa a ver o texto
		// que o modelo recebeu porque quem olha é um administrador. O modo DEV
		// acrescenta PROFUNDIDADE, nunca o direito de ignorar o dono.
		prompt: "showReasoning",
		tools: "showTools",
		toolIo: "showTools",
		toolRaw: "showTools",
		toolDuration: "showTools",
		toolMedia: "showTools",
		usage: "showCost",
		model: "showCost",
		cost: "showCost",
		credits: "showCost",
	};

/** Os interruptores ligam os eixos prometidos; consumo técnico fica restrito ao dev. */
const SWITCH_GRANTS: Record<
	"showReasoning" | "showTools" | "showCost",
	readonly AgentDisplayAxis[]
> = {
	showReasoning: ["reasoning"],
	showTools: ["tools", "toolDuration"],
	showCost: ["usage", "cost", "credits"],
};

const PREVIEW_KEY = "benvox.agentDisplay.previewAsClient";

/**
 * "Ver como cliente", ligado por um administrador da plataforma.
 *
 * Módulo e não componente: três superfícies montam o mesmo chat, e o interruptor
 * tem de as alcançar todas de uma vez. `localStorage` e nunca o servidor —
 * é a preferência DESTE browser a espreitar, e escrevê-la no servidor mudaria o
 * que outra pessoa vê. Ignorada para quem não é admin: não há nada a espreitar.
 */
const previewAsClient = ref(readPreview());

function readPreview(): boolean {
	try {
		return localStorage.getItem(PREVIEW_KEY) === "true";
	} catch {
		// Um browser com armazenamento bloqueado continua a conversar; só não guarda
		// a preferência entre sessões.
		return false;
	}
}

export function setPreviewAsClient(on: boolean): void {
	previewAsClient.value = on;
	try {
		localStorage.setItem(PREVIEW_KEY, String(on));
	} catch {
		// Idem: a sessão em curso respeita a escolha, a próxima recomeça no default.
	}
}

/**
 * A RESOLUÇÃO, sem Vue nenhum à volta: a lista da instalação mais os três
 * interruptores do workspace dão o que se desenha.
 *
 * Uma função e não um `computed` inline porque é a regra do módulo inteiro — dois
 * componentes atrás dela e um defeito que só aparecia com a política e o
 * interruptor a discordar (BUGS.md 05-2). Assim ela lê-se e testa-se sozinha.
 */
export function resolveAxes(
	wanted: AgentDisplayMode,
	switches: AgentDisplay,
	audience: Audience,
): AgentDisplayMode {
	const resolved = {} as Record<AgentDisplayAxis, boolean>;
	for (const axis of Object.keys(DEFAULT_CLIENT_MODE) as AgentDisplayAxis[]) {
		const gate = TENANT_SWITCH[axis];
		if (gate === undefined) {
			resolved[axis] = wanted[axis];
			continue;
		}
		// O E de sempre — o dono continua a poder apagar — e, quando ele LIGOU, o que
		// o próprio interruptor promete conta como permissão. Ver `SWITCH_GRANTS`.
		resolved[axis] = switches[gate] && (wanted[axis] || SWITCH_GRANTS[gate].includes(axis));
	}
	// O cliente vê créditos, mesmo se a política da instalação liberar dinheiro ou tokens.
	if (audience === "client") {
		resolved.cost = false;
		resolved.usage = false;
	}
	return resolved;
}

/**
 * Os eixos resolvidos para quem está a olhar.
 *
 * Devolve SEMPRE um registo completo, e nunca `undefined` enquanto carrega: a
 * conversa desenha no primeiro milissegundo, e um `v-if` sobre `undefined`
 * esconde o raciocínio de toda a gente até a rede responder — que é a mesma tela
 * de um cliente, mostrada ao dev, sem ninguém ter pedido.
 */
export function useAgentDisplayMode() {
	const access = usePlatformAccessDoc();
	const tenant = useAgentDisplay();

	const policy = computed(() => access.data.value?.agentDisplay ?? DEFAULT_POLICY);
	const isPlatformAdmin = computed(() => access.data.value?.platformAdmin === true);

	/** Se há sequer o que espreitar: só um admin, e só se a instalação deixar. */
	const canPreview = computed(() => isPlatformAdmin.value && policy.value.allowPreviewAsClient);
	const previewing = computed(() => canPreview.value && previewAsClient.value);

	const audience = computed<Audience>(() =>
		isPlatformAdmin.value && !previewing.value ? "dev" : "client",
	);

	const show = computed<AgentDisplayMode>(() =>
		resolveAxes(policy.value[audience.value], tenant.value, audience.value),
	);

	return { show, audience, canPreview, previewing, setPreviewAsClient };
}
